import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ConfiguracaoTab } from '@/components/pixel/ConfiguracaoTab';
import { MeusPixelsTab } from '@/components/pixel/MeusPixelsTab';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { usePixels } from '@/hooks/usePixels';
import { useFlowsList } from '@/hooks/vsl-dinamica/useDynamicFlows';
import { videoService } from '@/services/videoService';
import { Loader2, Target } from 'lucide-react';

type VideoOption = { id_video: string; nomeVideo: string };

export default function Pixel() {
  useLanguage();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const flowId = params.get('flowId') || undefined;
  const flows = useFlowsList();
  const selectedFlow = flows.data?.find((flow) => flow.id === flowId);
  const flow = flowId ? { id: flowId, name: selectedFlow?.name || 'Funil da VSL Dinâmica' } : undefined;
  const { pixels, isLoading, error, addPixel, deletePixel, setVideoNamesCache, fetchPixels } = usePixels(flowId, selectedFlow?.name);
  const [activeTab, setActiveTab] = useState('meus-pixels');
  const [videos, setVideos] = useState<VideoOption[]>([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState(!flowId);
  const [videoError, setVideoError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setVideoError(null);
      if (!user?.id_user || flowId) {
        setVideos([]);
        setIsLoadingVideos(false);
      } else {
        setIsLoadingVideos(true);
        try {
          const response = await videoService.getVideosByUserId(user.id_user);
          if (cancelled) return;
          const rows: VideoOption[] = response?.response || [];
          setVideos(rows);
          setVideoNamesCache(Object.fromEntries(rows.map((v) => [v.id_video, v.nomeVideo || 'Vídeo'])));
        } catch {
          if (cancelled) return;
          setVideos([]);
          setVideoError('Não foi possível carregar os vídeos. Recarregue a página para tentar novamente.');
        } finally {
          if (!cancelled) setIsLoadingVideos(false);
        }
      }
      if (!cancelled) await fetchPixels();
    };
    load();
    return () => { cancelled = true; };
  }, [user?.id_user, flowId, fetchPixels, setVideoNamesCache]);

  const selectScope = (value: string) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value === 'videos') next.delete('flowId');
      else next.set('flowId', value);
      return next;
    });
  };

  return (
    <Layout fixedSidebar>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-shrink-0 bg-transparent">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 pb-5">
            <div className="flex items-center gap-2.5">
              <Target className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} />
              <h1 className="text-[22px] font-semibold text-[#1E2330] tracking-tight">{translateInterfaceText("Pixel")}</h1>
            </div>
          </div>
          <div className="hidden lg:block h-px w-full bg-black/[0.08]" />
        </div>
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="mb-6 max-w-xl space-y-2">
              <Label htmlFor="pixel-scope">{translateInterfaceText("Rastrear pixels de")}</Label>
              <Select value={flowId || 'videos'} onValueChange={selectScope}>
                <SelectTrigger id="pixel-scope" aria-label={translateInterfaceText("Rastrear pixels de")}>
                  <SelectValue placeholder={translateInterfaceText("Selecione os vídeos ou um funil")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="videos">{translateInterfaceText("Vídeos normais")}</SelectItem>
                  {flows.data?.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
                  {flowId && !selectedFlow && <SelectItem value={flowId}>{translateInterfaceText("Funil selecionado")}</SelectItem>}
                </SelectContent>
              </Select>
              {flows.isError && <p role="alert" className="text-sm text-red-600">{translateInterfaceText("Não foi possível carregar os funis.")}{" "}<button type="button" className="underline" onClick={() => flows.refetch()}>{translateInterfaceText("Tentar novamente")}</button></p>}
              {flow && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-slate-700">
                  <p>{translateInterfaceText("Uma configuração acompanha todos os vídeos do funil publicado, incluindo os adicionados depois.")}</p>
                  <p className="mt-1 text-xs">{translateInterfaceText("Os eventos são enviados durante a reprodução pública. A pré-visualização não dispara pixels.")}</p>
                  <Link to={`/vsl-dinamica/${flow.id}`} className="mt-2 inline-block font-medium text-blue-700 hover:underline">{translateInterfaceText("Voltar ao funil")}</Link>
                </div>
              )}
            </div>
            {videoError && <p role="alert" className="mb-4 text-sm text-red-600">{videoError}</p>}
            {(isLoading || isLoadingVideos) ? (
              <div className="flex items-center justify-center py-16"><Loader2 aria-label={translateInterfaceText("Carregando pixels")} className="w-8 h-8 animate-spin text-[#437DFF]" /></div>
            ) : error ? (
              <div role="alert" className="rounded-lg border p-5 space-y-3">
                <p>{error}</p><Button variant="outline" onClick={() => fetchPixels()}>{translateInterfaceText("Tentar novamente")}</Button>
              </div>
            ) : (
              <Tabs key={`${user?.id_user}:${flowId || 'videos'}`} value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="mb-6">
                  <TabsTrigger value="meus-pixels">{translateInterfaceText("Meus Pixels")}</TabsTrigger>
                  <TabsTrigger value="configuracao">{translateInterfaceText("Configurações")}</TabsTrigger>
                </TabsList>
                <TabsContent value="meus-pixels" className="mt-0">
                  <MeusPixelsTab pixels={pixels} videos={videos} addPixel={addPixel} deletePixel={deletePixel} />
                </TabsContent>
                <TabsContent value="configuracao" className="mt-0">
                  <ConfiguracaoTab videos={videos} addPixel={addPixel} pixels={pixels} flow={flow} />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </main>
      </div>
    </Layout>
  );
}
